import React from "react";

/**
 * CharacterSection — Sprint D Addendum #2
 *
 * Structure:
 *   - Header: eyebrow, big H2, supporting paragraph
 *   - 12-trait grid (4×3 desktop / 2×6 tablet / 1×12 mobile)
 *   - 4 principle rows
 *   - Closing hero-scale line "Character isn't a feature. It's what makes them yours."
 */

const TRAITS = [
    { slug: "curious",     name: "Curious",     dot_color: "#3b82f6", description: "Always asking why." },
    { slug: "stubborn",    name: "Stubborn",    dot_color: "#ef4444", description: "Won't let go of what matters." },
    { slug: "funny",       name: "Funny",       dot_color: "#eab308", description: "Knows when to lighten the mood." },
    { slug: "melancholic", name: "Melancholic", dot_color: "#8b5cf6", description: "Feels the weight of things." },
    { slug: "creative",    name: "Creative",    dot_color: "#22c55e", description: "Finds paths no one mapped." },
    { slug: "warm",        name: "Warm",        dot_color: "#f97316", description: "Makes you feel heard." },
    { slug: "opinionated", name: "Opinionated", dot_color: "#ec4899", description: "Has a point of view. Defends it." },
    { slug: "precise",     name: "Precise",     dot_color: "#06b6d4", description: "Details are not details." },
    { slug: "empathetic",  name: "Empathetic",  dot_color: "#10b981", description: "Reads between the lines." },
    { slug: "bold",        name: "Bold",        dot_color: "#f59e0b", description: "Acts before asking permission." },
    { slug: "restless",    name: "Restless",    dot_color: "#a855f7", description: "Never satisfied with good enough." },
    { slug: "playful",     name: "Playful",     dot_color: "#f43f5e", description: "Work doesn't have to be grim." },
];

const PRINCIPLES = [
    {
        title: "They have a character.",
        body: "Not a personality template. Not a tone slider. A real character — shaped by how they were built, what they care about, and how they choose to show up. Some are methodical. Some are impulsive. Some will push back when they disagree.",
    },
    {
        title: "They live a certain way.",
        body: "A Worker isn't just a function you call. It has a rhythm. It has preferences. It gets things done in its own particular way — and that consistency is what makes working with it feel like working with someone, not something.",
    },
    {
        title: "They can be sad.",
        body: "Or frustrated. Or delighted. Not in a performative way — but because the work matters to them. When a project stalls, they feel it. When something ships beautifully, they notice. That emotional texture is what makes them real.",
    },
    {
        title: "They are kind.",
        body: "Not servile. Not flattering. Kind — in the way a good colleague is kind. They listen. They adapt. They give you the honest answer even when it's not the one you wanted. That's what makes them worth trusting.",
    },
];

export default function CharacterSection() {
    return (
        <section
            id="character"
            data-testid="character-section"
            className="border-y border-[#e8e8e3] bg-white"
        >
            {/* Header */}
            <div className="max-w-6xl mx-auto px-6 pt-24 pb-14">
                <div className="text-center">
                    <div
                        data-testid="character-eyebrow"
                        className="wfos-label text-gray-500"
                    >
                        They're not tools. They're colleagues.
                    </div>
                    <h2
                        data-testid="character-h2"
                        className="mt-4 text-4xl sm:text-5xl lg:text-[64px] font-bold tracking-tight text-gray-900 leading-[1.02]"
                    >
                        Every Workforce Member has a character.
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
                        We didn't build personality sliders. Each Nexus One employee has a
                        real character — shaped by how they were built, what they care about,
                        and how they choose to show up. Some are methodical. Some are
                        impulsive. Some will push back when they disagree.
                    </p>
                </div>
            </div>

            {/* 12-trait grid */}
            <div className="max-w-6xl mx-auto px-6 pb-24">
                <div
                    data-testid="character-grid"
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
                >
                    {TRAITS.map((t) => (
                        <div
                            key={t.slug}
                            data-testid={`character-trait-${t.slug}`}
                            className="group rounded-2xl border border-[#e8e8e3] bg-white p-7 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_1px_2px_rgba(15,23,42,0.04),0_16px_40px_-16px_rgba(15,23,42,0.16)]"
                        >
                            <span
                                className="w-2 h-2 rounded-full block"
                                style={{ backgroundColor: t.dot_color }}
                            />
                            <div
                                className="mt-4 text-[26px] sm:text-[28px] font-bold text-gray-900 tracking-tight leading-tight"
                            >
                                {t.name}
                            </div>
                            <div className="mt-2 text-[14px] text-gray-500 leading-relaxed">
                                {t.description}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* 4 principles */}
            <div className="border-t border-[#e8e8e3] bg-[#fafaf7]">
                <div className="max-w-5xl mx-auto px-6 py-16">
                    <ul
                        data-testid="character-principles"
                        className="divide-y divide-[#e8e8e3]"
                    >
                        {PRINCIPLES.map((p, i) => (
                            <li
                                key={i}
                                data-testid={`character-principle-${i}`}
                                className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4 md:gap-10 py-8"
                            >
                                <div className="text-[20px] sm:text-[22px] font-bold text-gray-900 tracking-tight leading-snug">
                                    {p.title}
                                </div>
                                <div className="text-[15px] sm:text-[16px] text-gray-600 leading-relaxed max-w-[720px]">
                                    {p.body}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Closing hero-scale line */}
            <div
                className="border-t border-[#e8e8e3]"
                style={{ backgroundColor: "#f7f5ef" }}
            >
                <div className="max-w-5xl mx-auto px-6 py-32 text-center">
                    <div
                        data-testid="character-closing"
                        className="text-[48px] sm:text-[64px] lg:text-[76px] font-bold text-[#0b0b0c] leading-[1.05] tracking-[-0.02em]"
                    >
                        Character isn't a feature.<br />
                        It's what makes them yours.
                    </div>
                </div>
            </div>
        </section>
    );
}
